import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCan } from "@/lib/auth/useCan";

import { useCreateQuarter, useQuarters, useUpdateQuarter } from "../api";

export function QuarterAdminPage() {
  const canAdmin = useCan("objectives.admin");
  const quarters = useQuarters();
  const create = useCreateQuarter();
  const update = useUpdateQuarter();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarterNum, setQuarterNum] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [error, setError] = useState<string | null>(null);

  if (!canAdmin) {
    return <p className="text-sm text-destructive">You don't have access to manage quarters.</p>;
  }

  function add() {
    setError(null);
    create.mutate(
      { year, quarter_num: quarterNum },
      { onError: (e) => setError(String(e instanceof Error ? e.message : e)) },
    );
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <Link to="/objectives" className="text-xs text-muted-foreground hover:underline">
          ← Back to objectives
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Manage quarters</h1>
        <p className="text-sm text-muted-foreground">
          Create quarters and close ones that are done. Closed quarters stay visible for review.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New quarter</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="q-year">Year</Label>
            <Input
              id="q-year"
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-28"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="q-num">Quarter</Label>
            <select
              id="q-num"
              value={quarterNum}
              onChange={(e) => setQuarterNum(Number(e.target.value))}
              className="h-9 rounded-xl border border-input bg-background px-2 text-sm"
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Q{q}
                </option>
              ))}
            </select>
          </div>
          <Button type="button" onClick={add} disabled={create.isPending}>
            Create {year}-Q{quarterNum}
          </Button>
          {error && <p className="w-full text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All quarters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col divide-y divide-border">
          {quarters.data?.map((q) => (
            <div key={q.id} className="flex items-center justify-between py-2.5">
              <div>
                <span className="font-semibold">{q.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {q.start_date} → {q.end_date}
                </span>
                {q.is_closed && (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    closed
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => update.mutate({ id: q.id, payload: { is_closed: !q.is_closed } })}
              >
                {q.is_closed ? "Reopen" : "Close"}
              </Button>
            </div>
          ))}
          {quarters.data?.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">No quarters yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
